import React, { useState, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  ContentLayoutVariant,
  Stage as StageData,
} from '@silvertours/common-landingpages-view';
import {
  HostLegacy,
  LayoutLegacy,
  useSiteInfo,
} from '@silvertours/front-legacy-entities';
import { MediaLegacy, Theme } from '@silvertours/front-legacy-shared';
import { useMedia } from 'react-use';
import { SearchForm } from '../SearchFormLegacy';
import {
  StageContentContainer,
  StageLink,
  StageWrapper,
  StyledH1,
  WizardSearchWrapper,
} from './Stage.styles';
import { StageContext } from './context';
import { SearchContainer } from './SearchContainer';
import { resolveStageVariantConfig } from './Stage.variant';

type StageProps = {
  variant: ContentLayoutVariant;
  content?: StageData;
  // TODO: Provide link from BFF
  link?: {
    href: string;
    target: string;
    title: string;
  };
  headline?: string;
};

const IMAGE_PATH_PREFIX = '/bm/global/files/images/';

const Stage = ({ variant, content, link, headline }: StageProps) => {
  const { abTests, isAbTestReady } = useSiteInfo();
  const activeVariant = isAbTestReady
    ? abTests.stage_image_visibility_test
    : '';
  const isStageVisibility = activeVariant === 'better_stage_image_visibility';

  // useMedia uses max-width while the mq function (used on the `WizardSearchWrapper` component) uses min-width; -1 aligns the boundary so both switch at the same breakpoint
  const isMobile = useMedia(
    `(max-width: ${(Theme.getBreakpoint('sm') ?? 576) - 1}px)`,
    true,
  );

  const {
    keyword,
    image: imageData,
    includeSearch,
    imageWithFocalPoint,
  } = content || {};

  const t = useTranslations('features.stageLegacy.stage');
  const { formatImage } = HostLegacy.useHost();

  const {
    isMinimal,
    isPlain,
    isErrorPage,
    isPromoPage,
    showImage,
    searchDisplayMode,
  } = resolveStageVariantConfig(variant);

  const defaultImage: MediaLegacy.ImageProps = {
    src: t('defaultImage.src'),
    alt: t('defaultImage.alt'),
    id: t('defaultImage.id'),
  };

  const image = imageWithFocalPoint || imageData || defaultImage;

  const [stageImage, setStageImage] = useState(image.src);

  const handleStageImageChange = useCallback(
    (src?: string) => {
      const imageSrc = src ? `${IMAGE_PATH_PREFIX}${src}` : defaultImage.src;
      setStageImage(imageSrc);
    },
    [defaultImage.src],
  );

  const value = useMemo(
    () => ({
      stageImage,
      setStageImage: handleStageImageChange,
    }),
    [handleStageImageChange, stageImage],
  );

  const newImage = { ...image };

  if (image === defaultImage) {
    newImage.src = formatImage(stageImage || image.src);
  }

  return (
    <StageWrapper isMinimal={isMinimal} isErrorPage={isErrorPage}>
      <LayoutLegacy.ImageSegment
        id="stage-image"
        image={newImage}
        isStage
        includeSearch={includeSearch}
        showImage={showImage}
        isMinimal={isMinimal}
        isPlain={isPlain}
        isErrorPage={isErrorPage}
      >
        <StageContentContainer
          isMinimal={isMinimal}
          isErrorPage={isErrorPage}
          isStageVisibility={isStageVisibility}
        >
          {isMobile && !isMinimal && (
            <WizardSearchWrapper>
              {!isErrorPage && <StyledH1>{headline}</StyledH1>}
              <SearchContainer
                image={newImage}
                includeSearch={includeSearch}
                isErrorPage={isErrorPage}
              />
            </WizardSearchWrapper>
          )}
          {!isMobile && (
            <StageContext.Provider value={value}>
              {Boolean(link?.href) && (
                <StageLink href={link?.href} target={link?.target}>
                  {link?.title}
                </StageLink>
              )}
              {includeSearch && (
                <SearchForm
                  headline={headline}
                  stageTitle={keyword}
                  isErrorPage={isErrorPage}
                  isPromoPage={isPromoPage}
                  displayMode={searchDisplayMode as 'full' | 'thin'}
                  isWide
                  isStageVisibility={isStageVisibility}
                />
              )}
            </StageContext.Provider>
          )}
        </StageContentContainer>
      </LayoutLegacy.ImageSegment>
    </StageWrapper>
  );
};

export { Stage };
export type { StageProps };
